/**
 * tourAnalytics — fires lightweight, non-blocking events to the backend
 * so we can later see which beacons help (and which don't).
 *
 * Why it's fire-and-forget:
 *   • Tour UX must never block on a network call.
 *   • The endpoint accepts anonymous events (no auth required).
 *   • Failures are silent — analytics MUST NOT break the customer flow.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let queue = [];
let flushScheduled = false;

function scheduleFlush() {
    if (flushScheduled) return;
    flushScheduled = true;
    // Debounced flush — 800ms after last event so we batch a typical
    // tour session (open + complete + next) into a single network call.
    setTimeout(async () => {
        flushScheduled = false;
        const batch = queue.splice(0, queue.length);
        if (!batch.length) return;
        try {
            await axios.post(
                `${API}/tour/events`,
                { events: batch },
                { withCredentials: true, timeout: 4000 },
            );
        } catch {
            // Silent — analytics never breaks UX.
        }
    }, 800);
}

/**
 * fireTourEvent — record a tour interaction.
 *
 * @param {string} type — "shown" | "opened" | "completed" | "dismissed" | "skipped" | "walk_started" | "walk_completed"
 * @param {string} beaconId — stable beacon id from tourCopy.json
 * @param {object} extra — optional fields (step, mode, elapsed_ms, etc.)
 */
export function fireTourEvent(type, beaconId, extra = {}) {
    const payload = {
        type,
        beacon_id: beaconId,
        at: new Date().toISOString(),
        pathname: typeof window !== "undefined" ? window.location.pathname : "",
        viewport: typeof window !== "undefined"
            ? `${window.innerWidth}x${window.innerHeight}` : "",
        ...extra,
    };
    queue.push(payload);
    scheduleFlush();
}

export default { fireTourEvent };
